import { useMemo, useState } from "react";
import "../uploadFile/upload.css";
import { z } from "zod";

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import ArchiveRoundedIcon from "@mui/icons-material/ArchiveRounded";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";


export function GlobalImportDocumentModal({
    open,
    handleClose,
    show,
    onClose,
    handleUpload,
    operationType = "carga",
    originScreen = "freight",
    uploadProgress = 0,
    validationSchema,
    allowedTypes,
    maxSizeMB = 10,
    errorMessage,
}) {

    const isVisible = open ?? show ?? false;
    const handleDismiss = handleClose ?? onClose;


    const [files, setFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const COLORS = useMemo(
        () => ({
            bg: "#F9FAFB",
            ink: "#012442",
            accent: "#ff3100",
            shadow: "0px 10px 40px rgba(0,0,0,0.14)",
            weight: 400,
        }),
        []
    );

    const isCargaOperation = operationType === "carga";

    const activeSchema = useMemo(() => {
        if (validationSchema) return validationSchema;

        if (allowedTypes?.length) {
            return z.object({
                name: z.string().min(1),
                size: z.number().max(maxSizeMB * 1024 * 1024, `O arquivo não pode ter mais de ${maxSizeMB}MB.`),
                type: z.string().refine(
                    (val) => allowedTypes.includes(val),
                    { message: errorMessage || "Formato de arquivo não permitido." }
                ),
            });
        }
        return null;
    }, [validationSchema, allowedTypes, maxSizeMB, errorMessage]);

    const cleanupState = () => {
        setFiles([]);
        setFilePreviews([]);
        setUploading(false);
        setValidationErrors([]);
    };

    const handleFileChange = (e) => {
        if (!e.target.files?.length) return;
        const selected = Array.from(e.target.files);
        setValidationErrors([]);

        if (activeSchema) {
            const errorsCaptured = [];
            const validFiles = [];

            selected.forEach((file) => {
                const result = activeSchema.safeParse({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                });

                if (result.success) {
                    validFiles.push(file);
                } else {
                    const issues = result.error.issues || result.error.errors || [];
                    issues.forEach((err) => {
                        if (!errorsCaptured.includes(err.message)) {
                            errorsCaptured.push(err.message);
                        }
                    });
                }
            });

            if (errorsCaptured.length > 0) {
                setValidationErrors(errorsCaptured);
            }

            if (validFiles.length > 0) {
                setFiles((prev) => [...prev, ...validFiles]);
                setFilePreviews((prev) => [
                    ...prev,
                    ...validFiles.map((f) => ({
                        url: URL.createObjectURL(f),
                        name: f.name,
                        type: f.type,
                    })),
                ]);
            }
        } else {
            setFiles((prev) => [...prev, ...selected]);
            setFilePreviews((prev) => [
                ...prev,
                ...selected.map((f) => ({
                    url: URL.createObjectURL(f),
                    name: f.name,
                    type: f.type,
                })),
            ]);
        }
        e.target.value = "";
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
        setFilePreviews((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleConfirmUpload = async () => {
        if (!files.length || !handleUpload) return;
        setUploading(true);
        try {
            for (const file of files) {
                await handleUpload(file);
            }
            cleanupState();
            if (handleDismiss) handleDismiss();
        } catch (err) {
            console.error("Erro no upload de documento:", err);
        } finally {
            setUploading(false);
        }
    };

    const getFileMeta = (fileName = "") => {
        const ext = fileName.split(".").pop()?.toLowerCase() || "";
        if (ext === "pdf") return { icon: <DescriptionRoundedIcon sx={{ color: COLORS.ink }} />, tag: "PDF" };
        if (["png", "jpg", "jpeg"].includes(ext)) return { icon: <ImageRoundedIcon sx={{ color: COLORS.ink }} />, tag: "Imagem" };
        if (ext === "zip") return { icon: <ArchiveRoundedIcon sx={{ color: COLORS.ink }} />, tag: "ZIP" };
        if (ext === "xml") return { icon: <DataObjectRoundedIcon sx={{ color: COLORS.ink }} />, tag: "XML" };
        return { icon: <DescriptionRoundedIcon sx={{ color: COLORS.ink }} />, tag: ext.toUpperCase() || "ARQ" };
    };

    const acceptAttribute = useMemo(() => {
        if (allowedTypes?.length) {
            return allowedTypes.join(",");
        }
        return undefined;
    }, [allowedTypes]);

    const typesDisplayLabel = useMemo(() => {
        if (allowedTypes?.length) {
            const formatted = allowedTypes
                .map((t) => t.replace("image/", "").replace("application/", "").toUpperCase())
                .join(" • ");
            return `Tipos: ${formatted}`;
        }
        return "Tipos: PDF • PNG • JPG • JPEG • XML • ZIP";
    }, [allowedTypes]);

    return (
        <Dialog
            open={isVisible}
            onClose={() => {
                cleanupState();
                if (handleDismiss) handleDismiss();
            }}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    bgcolor: COLORS.bg,
                    boxShadow: COLORS.shadow,
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "720px",
                },
            }}
        >
            <DialogTitle sx={{ px: 3, pt: 2.5, pb: 1.5, color: COLORS.ink, fontWeight: 700 }}>
                Importar documento(s) do {operationType}
                <Typography sx={{ mt: 0.5, color: "rgba(1,36,66,0.75)", fontSize: 13 }}>
                    Selecione arquivos válidos e confirme para enviar.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                    <Chip
                        size="small"
                        label={`Operação: ${isCargaOperation ? "Carga" : "Descarga"}`}
                        sx={{ bgcolor: "rgba(1,36,66,0.08)", color: COLORS.ink, fontWeight: 600 }}
                    />
                    <Chip
                        size="small"
                        label={typesDisplayLabel}
                        sx={{ bgcolor: "rgba(255,49,0,0.10)", color: COLORS.ink, fontWeight: 600 }}
                    />
                </Stack>
            </DialogTitle>

            <Divider />
            <DialogContent sx={{ px: 3, py: 2.5 }}>
                {validationErrors.length > 0 && (
                    <Alert
                        severity="error"
                        onClose={() => setValidationErrors([])}
                        sx={{
                            mb: 2,
                            borderRadius: 2.5,
                            fontWeight: 600,
                            fontSize: 13.5,
                            boxShadow: "0px 2px 8px rgba(255,49,0,0.12)",
                        }}
                    >
                        {validationErrors.map((err, idx) => (
                            <Box key={idx} sx={{ mb: idx === validationErrors.length - 1 ? 0 : 0.5 }}>
                                {err}
                            </Box>
                        ))}
                    </Alert>
                )}
                <Box
                    role="button"
                    onClick={() => document.querySelector("#modal-global-file-input")?.click()}
                    sx={{
                        borderRadius: 3,
                        p: 2.5,
                        bgcolor: "#fff",
                        border: "1px dashed rgba(1,36,66,0.25)",
                        boxShadow: "0px 1px 0px rgba(0,14,51,0.08)",
                        transition: "all .15s ease",
                        cursor: "pointer",
                        width: "100%",
                        "&:hover": {
                            borderColor: "rgba(1,36,66,0.45)",
                            boxShadow: "0px 8px 20px rgba(0,14,51,0.12)",
                            transform: "translateY(-1px)",
                        },
                    }}
                >
                    <input
                        id="modal-global-file-input"
                        type="file"
                        hidden
                        accept={acceptAttribute}
                        multiple={isCargaOperation}
                        onChange={handleFileChange}
                    />

                    {filePreviews.length === 0 ? (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2.5,
                                    bgcolor: "rgba(1,36,66,0.08)",
                                    display: "grid",
                                    placeItems: "center",
                                }}
                            >
                                <CloudUploadRoundedIcon sx={{ color: COLORS.ink, fontSize: 30 }} />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ color: COLORS.ink, fontWeight: 700, fontSize: 15 }}>
                                    Clique para selecionar ou arraste arquivos aqui
                                </Typography>
                                <Typography sx={{ color: "rgba(1,36,66,0.70)", fontSize: 13 }}>
                                    {isCargaOperation ? "Você pode enviar múltiplos arquivos." : "Envie apenas imagens."}
                                </Typography>
                            </Box>

                            <Chip
                                size="small"
                                label={isCargaOperation ? "Multi-upload" : "Único"}
                                sx={{ bgcolor: "rgba(1,36,66,0.08)", color: COLORS.ink, fontWeight: 700 }}
                            />
                        </Stack>
                    ) : (
                        <Box sx={{ width: "100%" }}>
                            <Typography sx={{ color: COLORS.ink, fontWeight: 800, fontSize: 14, mb: 1.5 }}>
                                Arquivos selecionados ({filePreviews.length})
                            </Typography>

                            <List disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                                {filePreviews.map((file, index) => {
                                    const meta = getFileMeta(file.name);

                                    return (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                mb: 1,
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 2,
                                                bgcolor: "#fff",
                                                border: "1px solid rgba(1,36,66,0.10)",
                                            }}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveFile(index);
                                                    }}
                                                    sx={{ color: COLORS.accent }}
                                                >
                                                    <DeleteOutlineRoundedIcon />
                                                </IconButton>
                                            }
                                        >
                                            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: "100%", pr: 4 }}>
                                                {meta.icon}
                                                <Typography noWrap sx={{ color: COLORS.ink, fontWeight: 700, fontSize: 13.5, flex: 1 }}>
                                                    {file.name}
                                                </Typography>
                                                <Chip size="small" label={meta.tag} sx={{ bgcolor: "rgba(1,36,66,0.08)", color: COLORS.ink, fontWeight: 700 }} />
                                            </Stack>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    )}

                    {uploading && (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={24} sx={{ color: COLORS.ink }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink }}>
                                Enviando arquivo(s)... ({uploadProgress}%)
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
                <Button
                    onClick={handleConfirmUpload}
                    disabled={files.length === 0 || uploading}
                    variant="contained"
                    sx={{
                        bgcolor: COLORS.ink,
                        color: "#fff",
                        fontWeight: 800,
                        borderRadius: 3,
                        px: 2.25,
                        py: 1.1,
                        boxShadow: COLORS.shadow,
                        "&:hover": { bgcolor: "#011B30" },
                    }}
                >
                    {uploading ? "Importando..." : `Importar ${files.length} arquivo(s)`}
                </Button>

                <Button
                    onClick={() => {
                        cleanupState();
                        if (handleDismiss) handleDismiss();
                    }}
                    variant="outlined"
                    sx={{
                        ml: 1,
                        borderColor: "rgba(255,49,0,0.55)",
                        color: COLORS.accent,
                        fontWeight: 800,
                        borderRadius: 3,
                        px: 2.1,
                        py: 1.1,
                        "&:hover": { borderColor: COLORS.accent, bgcolor: "rgba(255,49,0,0.08)" },
                    }}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default GlobalImportDocumentModal;
